import { useContext, useEffect, useMemo, useState } from "react";
import { ADModalBody, ADModalInfo, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { ManagedWindow, WindowStateActions } from "src/WindowManager/WindowState";
import { GlassInputGroup, GlassInputGroupTitle, GlassTextInputTransformer } from "./GlassInput";
import { ViewButtonText } from "src/propertyInfoTemplates/PropertyComponents";
import { parseGetCapabilities, XMLResponseToUTF8 } from "src/dataCollector/WFSCollector";
import mitSoft from '../mit-soft-settings.json';
import mitSoftVector from '../mit-soft-vector.json';
import { Spinner } from "react-bootstrap";
import { MapitStateContext } from "src/states/MapitState";
import { featureCollection, polygon } from "@turf/helpers";
import { parseFeatureLayer } from "src/managers/DynamicDataDrivenLayer";
import { SettingsManager } from "@viamap/viamap2-common";
import { GenerateGeomUtils } from "src/managers/GenerateGeomUtils";
import { wfsEndpoints } from "src/dataCollector/WFSCollectorEndpoints";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import ReactDataGrid from "react-data-grid";


type TestResults = {
   valid?: "success" | "error" | "confused" | "diff" | "warning"
   validTile?: "success" | "error" | "confused" | "diff" | "warning"
   time?: number;
   size?: number;
}

export function LayerTester(props: { window: ManagedWindow }) {
   let { state: mapitState } = useContext(MapitStateContext);

   let { dispatch: windowDispath } = useWindowContext();
   let [zoom, setZoom] = useState<number>(mapitState.map?.getZoom() || 0);
   let [center, setCenter] = useState<{ lat: number; lng: number }>(mapitState.map ? mapitState.map.getCenter() : { lat: 0, lng: 0 });
   let [layerFContent, setLayerFContent] = useState<(MITLayer & TestResults)[] | null>(null);
   let [fileError, setFileError] = useState<string[]>([]);
   let [loading, setLoading] = useState<boolean>(false);
   let [sortColumn, setSortColumn] = useState([{ columnKey: "label", direction: "ASC" }]);

   useEffect(() => {
      let realMap = mapitState.map?.getMapPop();
      if (realMap) {
         if (!realMap.getSource("layerTestSquare")) realMap.addSource("layerTestSquare", {
            type: "geojson",
            data: featureCollection([polygon(tileBoundsFromCoord(center.lat, center.lng, zoom) as any)])
         });
         if (!realMap.getLayer("layerTestSquare")) realMap.addLayer({
            id: "layerTestSquare",
            type: "fill",
            source: "layerTestSquare",
            paint: {
               "fill-color": "rgb(0, 255, 149)",
               "fill-opacity": 0.5
            }
         });
         return () => {
            realMap.removeLayer("layerTestSquare");
            realMap.removeSource("layerTestSquare");
         }
      }
   }, [center, zoom]);


   function validateFileContent(content: string | null): boolean {
      console.log("Invalid layer file format", content);
      let json = JSON.parse(content || "{}");
      if ("standardBackgroundLayers" in json) {
         setLayerFContent(json["standardBackgroundLayers"].filter((l) => l.type !== "vectorTile" && l.type !== "geojsonWFS"));
         setFileError([]);
         return true;
      }
      if ("customBackgroundLayers" in json) {
         setLayerFContent(json["customBackgroundLayers"].filter((l) => l.type !== "vectorTile" && l.type !== "geojsonWFS"));
         setFileError([]);
         return true;
      }
      if ("dynamicBackgroundLayers" in json) {
         setLayerFContent(json["dynamicBackgroundLayers"].filter((l) => l.type !== "vectorTile" && l.type !== "geojsonWFS"));
         setFileError([]);
         return true;
      }
      if ("vectorBackgroundLayers" in json) {
         setLayerFContent(json["vectorBackgroundLayers"].filter((l) => l.type !== "vectorTile" && l.type !== "geojsonWFS"));
         setFileError([]);
         return true;
      }
      setFileError((pre) => [...pre, "Invalid layer file format."]);
      return false;
   }

   async function testLayers() {
      setLoading(true);
      let layers = layerFContent as MITLayer[];
      let capabilitiesURL = new Set<string>();
      let capabilities: {
         url: string;
         layers: string[];
      }[] = []
      layers.map((layer) => {
         let url = getCapabilitiesURL(layer)
         if (url) {
            capabilitiesURL.add(url);
         }
      });
      console.log("Testing layers with capabilities URLs:", capabilitiesURL);
      await Promise.allSettled([...capabilitiesURL].map((url) => {
         return new Promise((resolve) => {
            fetch(url).then((response) => {
               return response
            }).then(async (result) => {
               if (!result) {
                  return capabilities.push({ url: url, layers: [] });
               }
               let xmlText = await XMLResponseToUTF8(result);
               if (xmlText) {
                  let layers = parseGetCapabilities(xmlText || "", true).flatMap((a) => {
                     return a.includes(":") ? [a, a.split(":").at(-1) || ""] : [a];
                  }).filter(Boolean);
                  capabilities.push({ url: url, layers: layers.map(l => l?.toLowerCase() || "") });
               }
               resolve(null);
            }).catch(() => {
               capabilities.push({ url, layers: [] });
               resolve(null);
            });
         });
      }));
      setLayerFContent(layers.map((layer) => {
         let result
         let url = getCapabilitiesURL(layer);
         let layerARR = "src" in layer ? layer.src?.match?.(/TYPENAMES=([^&]+)/)?.[1] : (typeof layer.layers === "string" ? layer.layers : undefined);
         let allLayersID = layerARR ? layerARR.split(",") : [];
         let capability = capabilities.find(c => c.url === url);
         let capLayers = capability?.layers || [];
         if (capability) {
            if (allLayersID.every(id => capLayers.includes(id.toLowerCase()) || capLayers.includes(id.toLowerCase().split(":").at(-1) || ""))) {
               result = "success";
            } else {
               console.log("Layer test warning for layer:", layer.label, "Expected layers:", allLayersID, "Capabilities layers:", capLayers);
               result = "error";
            }
         } else {
            result = "confused";
         }
         return {
            ...layer,
            valid: result
         }
      }));
      setLoading(false);
   }

   async function testTiles() {
      let bounds = tileBoundsFromCoord(center.lat, center.lng, zoom)[0] as any;
      for (let layer of layerFContent || []) {
         if (layer.valid == "success") {
            continue;
         }
         let url = getTileUrl(layer);
         let preparedUrl = insertBounds(url, [bounds[0], bounds[2]]);
         if (preparedUrl.includes("undefined") || preparedUrl == "") {
            // WFS lag har ikke en tile url, TODO
            setLayerFContent((pre) => pre?.map(l => {
               if (l.label === layer.label) {
                  return { ...l, validTile: "confused" };
               } return l;
            }) || null);
            continue;
         }



         let startTime = new Date();
         let time: number;
         let size: number;
         let validTile: "success" | "error" | "confused" | "diff" | "warning" = "confused";
         try {
            let response = await fetchTile(preparedUrl, new AbortController());
            if (response.ok) {
               validTile = "success";
               let blob = await response.blob();
               time = new Date().getTime() - startTime.getTime();
               size = blob.size / 1024;
            } else {
               // console.log("Tile fetch error for layer:", layer.label, "URL:", preparedUrl, "Status:", response.status);
               validTile = "error";
            }
         } catch {
            // console.log("Tile fetch exception for layer:", layer.label, "URL:", preparedUrl, "Error:", e);
            validTile = "error";
         } finally {
            time ??= new Date().getTime() - startTime.getTime();;
         }
         setLayerFContent((pre) => pre?.map(l => {
            if (l.label === layer.label) {
               return { ...l, validTile: validTile, time, size };
            } return l;
         }) || null);
      }
   }

   const sortedFContent = useMemo(() => {
      if (!layerFContent) return null;
      if (sortColumn.length === 0) return layerFContent;
      let { columnKey, direction } = sortColumn[0];
      return layerFContent.toSorted((a, b) => {
         if (a[columnKey] == null) return 1;
         if (b[columnKey] == null) return -1;
         if (a[columnKey] == b[columnKey]) return 1;
         if (a[columnKey] > b[columnKey]) return direction === "ASC" ? 1 : -1;
         return direction === "ASC" ? -1 : 1;
      });
   }, [layerFContent, sortColumn]);

   return (
      <AdvancedDragModal window={props.window}
         title={"Layer Tester"}
         onClose={() => { windowDispath({ type: WindowStateActions.Close, windowId: props.window.id }) }}
         PosDefault={{ left: "50%", top: "5%", transform: "translate(-50%, 0%)" }}
         subHeaderContent={<>
            {<ViewButtonText onClick={() => {
               mapitState.map?.once("click", (e) => {
                  setCenter({ lat: e.lngLat.lat, lng: e.lngLat.lng });
                  setZoom(mapitState.map?.getZoom() || 0);
               })
            }} >Move square (Map select)</ViewButtonText>}
            {layerFContent ? <ViewButtonText onClick={() => { setLayerFContent(null); }} >Close file</ViewButtonText> : <></>}
            {layerFContent ? (loading ? <Spinner animation="border" size="sm" /> : <ViewButtonText onClick={() => { testLayers(); }} >Capabilities</ViewButtonText>) : <> </>}
            {layerFContent ? (loading ? <Spinner animation="border" size="sm" /> : <ViewButtonText onClick={() => { testTiles(); }} >Tile</ViewButtonText>) : <> </>}
         </>}
      >
         <ADModalBody>

            {fileError.length > 0 && (
               <ADModalInfo>
                  {fileError.map((error, index) => (
                     <div style={{ color: " red" }} key={index}>{error}</div>
                  ))}
               </ADModalInfo>
            )}
            <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", justifyContent: "space-around" }} >
               <CapabilityResults layers={layerFContent} />
               <TileResults layers={layerFContent} />
            </div>
            {layerFContent == null ?
               <GlassInputGroup>
                  <GlassInputGroupTitle>Upload or Select layer file</GlassInputGroupTitle>
                  <GlassTextInputTransformer label={"Upload layer file"}>
                     <input
                        type="file"
                        accept=".json"
                        onChange={(e) => {
                           if (e.target.files && e.target.files.length > 1) {
                              setFileError((pre) => [...pre, "Please select only one file."]);
                              return;
                           }
                           const file = e.target.files?.[0];
                           if (file) {
                              const reader = new FileReader();
                              reader.onload = (event) => {
                                 const content = event.target?.result as string;
                                 validateFileContent(content);
                              };
                              reader.readAsText(file);
                           }
                        }
                        }
                        placeholder="Enter layer file content here"
                     />
                  </GlassTextInputTransformer>
                  <GlassTextInputTransformer label={"Select MIT Soft layers"}>
                     <button onClick={() => {
                        setLayerFContent(structuredClone(mitSoft.standardBackgroundLayers) as (MITLayer & TestResults)[]);
                     }} >Standard</button>
                     <button onClick={() => {
                        setLayerFContent(structuredClone(mitSoftVector.vectorBackgroundLayers) as (MITLayer & TestResults)[]);
                     }} >Vector</button>
                  </GlassTextInputTransformer>

               </GlassInputGroup>
               : <></>}
            <ReactDataGrid
               className='rdg-light'
               style={{ flex: 1 }}
               rowKeyGetter={(row) => row.label}
               sortColumns={sortColumn as any}
               onSortColumnsChange={(e) => {
                  setSortColumn(e.slice(-1));
               }}
               defaultColumnOptions={{
                  sortable: true,
                  draggable: true,
                  resizable: true,
               }}
               columns={[
                  {
                     key: "valid", name: "Capability", width: 50, renderCell(props) {
                        return <div title={props.row.valid} className="wfsCell" style={{ color: resultColors(props.row.valid) }} >{wfsRes(props.row.valid)}</div>
                     }
                  },
                  {
                     key: "validTile", name: "Tile", width: 50, renderCell(props) {
                        return <div title={props.row.validTile} className="wfsCell" style={{ color: resultColors(props.row.validTile) }} >{wfsRes(props.row.validTile)}</div>
                     }
                  },
                  {
                     key: "label", name: "Layer", renderCell(props) {
                        return <div className="layerCell" style={{ color: resultColors(props.row.valid) }} >{props.row["label"]}</div>
                     }
                  },
                  { key: "time", name: "Tile load time" },
                  { key: "size", name: "Tile size (KB)" },
               ]}
               rows={sortedFContent || []} />
         </ADModalBody>
      </AdvancedDragModal>
   )
}

function CapabilityResults({ layers }: { layers: (MITLayer & TestResults)[] | null }) {

   let counts = useMemo(() => {
      let counts = {}
      for (let layer of layers || []) {
         if (layer.valid) {
            counts[layer.valid] = (counts[layer.valid] || 0) + 1;
         }
      }
      return counts;
   }, [layers]);


   return (
      <>
         <div style={{ fontWeight: "bold" }} >Capabilities results:

            {Object.entries(counts).map(([key, value]) => (
               <div key={key} style={{ color: resultColors(key) }} >{resultIcons(key)} {key}: {value + ""}</div>
            ))}
         </div>
      </>
   )

}



function TileResults({ layers }: { layers: (MITLayer & TestResults)[] | null }) {

   let counts = useMemo(() => {
      let counts = {}
      for (let layer of layers || []) {
         if (layer.validTile) {
            counts[layer.validTile] = (counts[layer.validTile] || 0) + 1;
         }
      }
      return counts;
   }, [layers]);


   return (
      <>
         <div style={{ fontWeight: "bold" }} >Tile results:

            {Object.entries(counts).map(([key, value]) => (
               <div key={key} style={{ color: resultColors(key) }} >{resultIcons(key)} {key}: {value + ""}</div>
            ))}
         </div>
      </>
   )

}

function wfsRes(result: any): string {
   const icons = {
      "success": "sss",
      "error": "eee",
      "confused": "ccc",
      "diff": "ddd",
      "warning": "www"
   };
   return icons[result] || "nnn";
}

function resultColors(result: any): string {
   const colors = {
      "success": "rgb(0, 136, 79)",
      "error": "rgb(139, 0, 0)",
      "confused": "rgb(170, 170, 0)",
      "diff": "rgb(0, 124, 124)",
      "warning": "rgb(138, 112, 65)"
   };
   return colors[result] || "rgb(63, 63, 63)";
}

function resultIcons(result: any): string {
   const icons = {
      "success": "✅",
      "error": "❌",
      "confused": "🤔",
      "diff": "🔄",
      "warning": "⚠️"
   };
   return icons[result] || "⁉️";
}

type MITLayer = WMSLayer | WFSLayer;

type WMSLayer = {
   label: string;
   type: undefined;
   layers: string;
   version: string;
   host: string;
   path: string;
   urlParams: { [key: string]: string };
}

type WFSLayer = {
   label: string;
   type: "geojsonWFS";
   src: string;
}

function LayerLine({ layer, center, zoom }: { layer: MITLayer & TestResults, center: { lat: number, lng: number }, zoom: number }) {

   return (
      <div className="TestLine" >
         <div>{layer.label}</div>
         <div className="wfs">{wfsRes(getWFSReference(layer))}</div>
         <div className="proxy" title="proxy">{getTileUrl(layer).includes("proxy://") ? <MIBIcon icon="proxyLayer" /> : <MIBIcon icon="directLayer" />}</div>
         <div className="status">{resultIcons(layer.valid)}</div>
         <button className="capability" onClick={() => {
            let url = getCapabilitiesURL(layer);
            if (url) {
               window.open("https://" + url.split("://").at(-1), "_blank");
            }
         }} >🌍</button>
         <div className="status">{resultIcons(layer.validTile)}</div>
         <div className="time">{layer.time ?? "?"} ms</div>
         <div className="size">{(typeof layer.size === "number") ? Math.round(layer.size) : "?"} KB</div>
         <button className="fetch" onClick={async () => {

            let polygon = tileBoundsFromCoord(center.lat, center.lng, zoom)[0];
            await fetchTile(insertBounds(getTileUrl(layer), [polygon[0], polygon[2]] as any), new AbortController())
         }} >🖼️</button>
         {/* <button className="capability" onClick={() => open(getCapabilitiesURL(layer))} >🌍</button> */}
         {/* <div className="tileUrl">{getTileUrl(layer)}</div> */}
      </div>
   )
}

function getTileUrl(layer: MITLayer): string {
   const tileUrl = parseFeatureLayer(layer)?.getTileURL() || "";
   console.log("Getting tile URL for layer:", layer);
   return tileUrl;
}

function insertBounds(url: string, bounds: [[number, number], [number, number]]): string {
   let webMecator = bounds.map((coord) => GenerateGeomUtils.convertWGS84ToWebMercator([coord[1], coord[0]]));

   let urlObj = url.replace("{bbox-epsg-3857}", `${webMecator[0][1]},${webMecator[1][0]},${webMecator[1][1]},${webMecator[0][0]}`);
   return urlObj;
}

function fetchTile(url: string, abortController: AbortController): Promise<Response> {
   if (url.startsWith("proxy")) {
      const corsProxy = SettingsManager.getSystemSetting("ImageCorsProxyUrl")
      return fetch(corsProxy + `https://${url.split("://")[1]}`, {
         headers: { "Accept": "image/png" },
         signal: abortController.signal
      })
   }
   return fetch(`https://${url.split("://")[1]}`, {
      headers: { "Accept": "*/*" },
      signal: abortController.signal
   })
}

function getWFSReference(layer: MITLayer): string {
   if ("wfs" in layer) {
      let wfsInfo = layer.wfs;
      if (wfsInfo && wfsInfo["key"] && wfsInfo["type"]) {
         if (Array.isArray(wfsInfo["type"])) {
            let co = wfsInfo["type"].filter((type) => wfsEndpoints[wfsInfo["key"]]?.["featureTypes"]?.[type]);
            return co.length === wfsInfo["type"].length ? "success" : (co.length > 0 ? "warning" : "error");
         } else if (wfsEndpoints[wfsInfo["key"]]?.["featureTypes"]?.[wfsInfo["type"]]) {
            return "success";
         } else {
            return "error";
         }
      }
      return "confused"
   }
   return "warning"
}

function getCapabilitiesURL(layer: MITLayer): string {
   let url = "";
   if (layer.type === undefined) {
      if ("host" in layer && "path" in layer) {
         url = `${layer.host}${layer.path}?service=WMS&request=GetCapabilities&version=${layer.version || "1.3.0"}` + (layer.urlParams ? Object.entries(layer.urlParams).map(([key, value]) => `&${key}=${value}`).join('') : "");
      }
   } else if (layer.type === "geojsonWFS" && "src" in layer) {
      let urlObj = new URL(layer.src);
      let keys = [...urlObj.searchParams.keys()]
      let notDelete = ["VERSION", "version", "username", "password"];
      keys.filter((a) => !notDelete.includes(a)).forEach((key) => {
         urlObj.searchParams.delete(key);
      });
      urlObj.searchParams.append("service", "WFS");
      urlObj.searchParams.append("request", "GetCapabilities");
      url = urlObj.toString();
   }
   if (url) {
      return "https://" + url.split("://").at(-1);
   }
   return "";
};

function tileBoundsFromCoord(lat, lon, zoom) {
   // const tileSize = 256;
   const scale = 1 << (zoom + 1); // 2^zoom

   // Convert lat/lon to tile X/Y
   const xTile = Math.floor((lon + 180) / 360 * scale);
   const yTile = Math.floor(
      (1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2 * scale
   );

   // Convert tile X/Y back to lat/lon bounds
   const left = xTile / scale * 360 - 180;
   const right = (xTile + 1) / scale * 360 - 180;

   const n = Math.PI - 2 * Math.PI * yTile / scale;
   const top = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));

   const n2 = Math.PI - 2 * Math.PI * (yTile + 1) / scale;
   const bottom = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n2) - Math.exp(-n2)));

   return [[[left, top], [right, top], [right, bottom], [left, bottom], [left, top]]];
}




